/**
 * Skills Service
 * Team skills: the procedures agents load on demand.
 *
 * See specs/ZM-047-skills.md
 */

import { TeamScopedService } from './base.service';

/**
 * Skill list item (from API)
 */
export interface SkillListItem {
  id: string;
  name: string;
  description: string;
  version: number;
  bodySizeBytes: number;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

/**
 * Skill including its body markdown
 */
export interface SkillDetail extends SkillListItem {
  body: string;
}

interface ListSkillsResponse {
  success: boolean;
  data?: { skills: SkillListItem[] };
  error?: string;
}

interface GetSkillResponse {
  success: boolean;
  data?: SkillDetail;
  error?: string;
}

interface MutateSkillResponse {
  success: boolean;
  data?: SkillListItem;
  error?: string;
}

interface DeleteSkillResponse {
  success: boolean;
  data?: { id: string; deleted: boolean };
  error?: string;
}

/**
 * Must match SKILL_DESCRIPTION_MAX_LENGTH in @zenmako/contracts/skills.
 *
 * Descriptions for every skill sit in the agent's context permanently, which
 * is what this cap protects.
 */
export const SKILL_DESCRIPTION_MAX_LENGTH = 140;

/**
 * Rough token estimate for one skill's always-on context cost:
 * description + name + list formatting.
 */
export function estimateSkillTokens(skill: SkillListItem): number {
  const chars = skill.description.length + skill.name.length + 6;
  return Math.ceil(chars / 4);
}

class SkillsService extends TeamScopedService {
  async listSkills(teamId: string): Promise<SkillListItem[]> {
    const response = await this.getTeam<ListSkillsResponse>(teamId, '/skills');
    return response.data?.skills || [];
  }

  async getSkill(teamId: string, skillId: string): Promise<SkillDetail | null> {
    const response = await this.getTeam<GetSkillResponse>(
      teamId,
      `/skills/${encodeURIComponent(skillId)}`
    );
    return response.data || null;
  }

  async createSkill(
    teamId: string,
    input: { name: string; description: string; body: string }
  ): Promise<MutateSkillResponse> {
    return this.postTeam<MutateSkillResponse>(teamId, '/skills', input);
  }

  async updateSkill(
    teamId: string,
    skillId: string,
    updates: { description?: string; body?: string }
  ): Promise<MutateSkillResponse> {
    return this.patchTeam<MutateSkillResponse>(
      teamId,
      `/skills/${encodeURIComponent(skillId)}`,
      updates
    );
  }

  async deleteSkill(teamId: string, skillId: string): Promise<DeleteSkillResponse> {
    return this.deleteTeam<DeleteSkillResponse>(
      teamId,
      `/skills/${encodeURIComponent(skillId)}`
    );
  }
}

export const skillsService = new SkillsService();
